import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import type { TankersEventDataBM } from '../../types/BusinessModel/TankersEventDataBM';
import { buildQueryParams } from '../../api/endpoints/queryParamsHelper';
import { AppFeatureEnum } from '../../enums/appFeatureEnum';
import type { AppFeatureParams } from '../../types/AppFeatureParams';
import { OfflineLevelEnum } from '../../enums/offlineLevelEnum';
import type { Vessel } from '../../types/ViewModel/Vessel/Vessel';
import type { MasterStowagePlanLoadResult } from '../../api/stowage/useGetMasterPlanApi';
import type { EventDataForVoyages } from '../../context/EventContext';
import type { RecoveryDomain, RecoveryDomainState, ReconnectNotice } from '../../context/ConnectionContext';
import type { ConnectionSyncState } from '../../components/page-header/connection-status/connectionStatusModel';
import type { iApiEndpoint } from '../../context/ApiEndpointsContext';
import type { ConnectionGeneration } from '../connectionRecoveryGuard';
import type { EventApplicationScope } from '../useSignalRConnection';
import { ConnectionRecoveryCoordinator } from './connectionRecoveryCoordinator';
import { MasterPlanVersionObservation, StowageMasterPlanVersionRecovery } from './stowageRecovery';
import { getNextEventRecoveryWatermarks, getOverlappingRecoveryEpoch } from './eventRecovery';
import { getEditableRecoveryDecision } from './editableRecoveryGuard';
import { getAllReportEventsItems } from '../../api/report-events/offlineReportEventsApi';
import {
    AOL_BUNKER_RECONNECT_NOTICE,
    isAolOrBunkerPath,
    RECONNECT_NOTICE_TOAST_ID,
    resolveAolBunkerCacheEndpoints,
} from './aolBunkerRecovery';

type RecoveryAdapterResult = 'recovered' | 'pending' | 'not-applicable' | 'failed';

interface ApiGetProps {
    endpoint?: string | null;
    queryParam?: string | null;
    callInBackground?: boolean;
    forceRefreshCache?: boolean | null;
}

export interface ConnectionRecoveryOrchestratorOptions {
    // Refs shared with useSignalRConnection.ts — the SAME mutable objects, so reads/writes here
    // and there observe the identical state. Never replace these with copies.
    selectedVesselRef: MutableRefObject<Vessel | null>;
    loggedInUserRef: MutableRefObject<unknown>;
    hubConnectionRef: MutableRefObject<HubConnection | null | undefined>;
    eventDataForVoyageListRef: MutableRefObject<EventDataForVoyages | null>;
    locationRef: MutableRefObject<string>;
    recoveryRequiredRef: MutableRefObject<boolean>;
    hasConnectedOnceRef: MutableRefObject<boolean>;
    wasOfflineOrDegradedRef: MutableRefObject<boolean>;
    reconnectRecoveryCoordinatorRef: MutableRefObject<ConnectionRecoveryCoordinator>;
    stowageMasterPlanVersionRecoveryRef: MutableRefObject<StowageMasterPlanVersionRecovery>;

    // Generation/scope helper — shared with connection/message-handling code.
    isCurrentGeneration: (generation: ConnectionGeneration) => boolean;
    getCurrentGeneration: () => ConnectionGeneration;

    // Event application — isCurrentEventApplicationScope and serializeEventApplication remain
    // shared with SignalR message handling (eventMessageHandler) in useSignalRConnection.ts, since
    // both a live message and a reconnect/reconciliation fetch must serialize onto the same
    // durable Event-consumer commit tail.
    isCurrentEventApplicationScope: (scope: EventApplicationScope | undefined, dto?: TankersEventDataBM) => scope is EventApplicationScope;
    serializeEventApplication: (scope: EventApplicationScope, apply: () => Promise<void>) => Promise<void>;
    updateDeltaEventArray: (
        tankersEventDataBM: TankersEventDataBM,
        offlineReportEvents: unknown[],
        imoNumber?: number,
        isCurrent?: () => boolean
    ) => Promise<string[]>;

    // A transaction that settled without being applied (no Event consumer mounted, or a mounted
    // consumer's backlog overflowed) flags its vessel via markEventReconciliationNeeded instead of
    // silently dropping the change. eventDataForVoyageList/eventConsumerCount/
    // eventReconciliationVersion are the events that can make reconciliation possible again, and
    // are the reconciliation effect's dependency list below.
    eventDataForVoyageList: EventDataForVoyages | null;
    eventConsumerCount: number;
    eventReconciliationVersion: number;
    isEventReconciliationNeeded: (imoNumber: number) => boolean;
    getEventReconciliationGeneration: (imoNumber: number) => number;
    clearEventReconciliationNeeded: (imoNumber: number, expectedGeneration?: number) => void;

    // State setters
    setEventDataForVoyageList: (data: EventDataForVoyages) => void;
    setRecoveryDomainState: (domain: RecoveryDomain, state: RecoveryDomainState) => void;
    setSyncState: (state: ConnectionSyncState) => void;
    setReconnectNotice: (notice: ReconnectNotice | null) => void;
    clearReconnectNotice: () => void;

    // API / feature dependencies
    getApiData: (props: ApiGetProps) => Promise<unknown>;
    apiEndpoints: iApiEndpoint[];
    removeRequestsContaining: (endpoints: string[], queryParams?: string) => Promise<void>;
    isFeatureEnabled: (params: AppFeatureParams) => boolean;
    getActiveMasterPlanVersion: (imoNumber: number) => Promise<MasterPlanVersionObservation | undefined>;
    getMasterStowagePlanFromApi: (vessel: Vessel, forceRefreshCache?: boolean, isCurrent?: () => boolean) => Promise<MasterStowagePlanLoadResult>;

    // Toast
    toastInfo: (text: string, duration?: number, toastId?: string) => void;
    dismissToast: (toastId?: string) => void;

    // Connectivity state (fresh every render, exactly as consumed before extraction)
    offlineLevel: OfflineLevelEnum;
    apiUnavailable: boolean;

    // Connection lifecycle — stays in useSignalRConnection.ts. Passed in directly (not via ref):
    // by the time it is actually invoked (async message handling / effects), the render that
    // supplied it has already finished, so the reference is never stale despite the ordinary
    // JS closure/const-initialization rules that allow useSignalRConnection.ts to declare
    // joinVesselGroup either before or after calling this hook.
    joinVesselGroup: (connection: HubConnection, imoNumber: number) => Promise<void>;
}

export interface ConnectionRecoveryOrchestrator {
    runReconnectRecovery: (scope: ConnectionGeneration) => Promise<void>;
    primeStowageMasterPlanVersion: (scope: ConnectionGeneration) => Promise<void>;
    applyEventChanges: (dto: TankersEventDataBM, scope: EventApplicationScope, appliedSignalREpoch?: number) => Promise<void>;
}

/**
 * Owns reconnect recovery orchestration: Event delta recovery (HTTP overlap + durable
 * Event-consumer commit), Stowage MasterPlanVersion recovery, AOL/Bunker cache eviction +
 * reconnect notice, the debounced connectivity-recovery trigger, and the dual online-detection
 * paths (React offlineLevel effect + raw window online/offline listeners).
 *
 * Invariants a caller must not break:
 * - Generation/session guards (connection, vessel, session axes) via isCurrentGeneration must
 *   gate every async continuation, since a vessel/session change can happen mid-await.
 * - ConnectionRecoveryCoordinator serializes concurrent reconnect attempts; do not call its
 *   recover logic outside `run`.
 * - StowageMasterPlanVersionRecovery's reload-then-recheck-once logic depends on being driven
 *   only through `recover`.
 * - Recovery step ordering (events -> stowage -> AOL/Bunker cache eviction -> notice) reflects
 *   dependency, not convention: AOL/Bunker eviction assumes Stowage/Events have already resolved.
 */
export const useConnectionRecoveryOrchestrator = (options: ConnectionRecoveryOrchestratorOptions): ConnectionRecoveryOrchestrator => {
    const {
        selectedVesselRef,
        loggedInUserRef,
        hubConnectionRef,
        eventDataForVoyageListRef,
        locationRef,
        recoveryRequiredRef,
        hasConnectedOnceRef,
        wasOfflineOrDegradedRef,
        reconnectRecoveryCoordinatorRef,
        stowageMasterPlanVersionRecoveryRef,
        isCurrentGeneration,
        getCurrentGeneration,
        isCurrentEventApplicationScope,
        serializeEventApplication,
        updateDeltaEventArray,
        eventDataForVoyageList,
        eventConsumerCount,
        eventReconciliationVersion,
        isEventReconciliationNeeded,
        getEventReconciliationGeneration,
        clearEventReconciliationNeeded,
        setEventDataForVoyageList,
        setRecoveryDomainState,
        setSyncState,
        setReconnectNotice,
        clearReconnectNotice,
        getApiData,
        apiEndpoints,
        removeRequestsContaining,
        isFeatureEnabled,
        getActiveMasterPlanVersion,
        getMasterStowagePlanFromApi,
        toastInfo,
        dismissToast,
        offlineLevel,
        apiUnavailable,
        joinVesselGroup,
    } = options;

    // Only used within this orchestrator's own debounce check — no other code reads/writes it.
    const lastConnectivityRecoveryAtRef = useRef(0);

    const isCurrentRecoveryScope = (scope: ConnectionGeneration): boolean =>
        isCurrentGeneration(scope) && !!loggedInUserRef.current && selectedVesselRef.current?.imoNumber !== undefined;

    // Durable Event delta merge, shared between reconnect recovery (recoverEvents below) and
    // SignalR live message handling (eventMessageHandler in useSignalRConnection.ts, which calls
    // this via the returned ConnectionRecoveryOrchestrator).
    const applyEventChanges = async (dto: TankersEventDataBM, scope: EventApplicationScope, appliedSignalREpoch?: number): Promise<void> => {
        if (!isCurrentEventApplicationScope(scope, dto)) {
            return;
        }

        let committedEventIds: string[] = [];
        if ((dto.events?.length ?? 0) > 0) {
            if (!isCurrentEventApplicationScope(scope, dto)) {
                return;
            }
            const offlineReportEvents = await getAllReportEventsItems();
            if (!isCurrentEventApplicationScope(scope, dto)) {
                return;
            }
            // The promise resolves only after useEventDataManagement (or the active
            // event consumer) has committed this transaction into fullEventArray.
            // A React setter call alone is not an application acknowledgement.
            committedEventIds = await updateDeltaEventArray(dto, offlineReportEvents, dto.imoNumber, () => isCurrentEventApplicationScope(scope, dto));
        }

        if (!isCurrentEventApplicationScope(scope, dto)) {
            return;
        }
        if (committedEventIds.length === 0) {
            return;
        }

        const currentEventData = eventDataForVoyageListRef.current;
        if (appliedSignalREpoch && Number.isSafeInteger(appliedSignalREpoch) && currentEventData?.imoNumber === scope.vesselImoNumber) {
            setEventDataForVoyageList({
                ...currentEventData,
                ...getNextEventRecoveryWatermarks(currentEventData, undefined, appliedSignalREpoch),
            });
        }
    };

    const recoverEvents = async (
        scope: ConnectionGeneration,
        onEventApplicationSettled?: (outcome: 'committed' | 'failed') => void
    ): Promise<RecoveryAdapterResult> => {
        const eventScope = eventDataForVoyageListRef.current;
        const selectedImoNumber = selectedVesselRef.current?.imoNumber;
        if (!selectedImoNumber || eventScope?.imoNumber !== selectedImoNumber || !eventScope.voyageNumbers || eventScope.voyageNumbers.length === 0) {
            return 'not-applicable';
        }

        const result = (await getApiData({
            endpoint: apiEndpoints['getTankersEventDataDeltaForVoyageList']?.url,
            queryParam: buildQueryParams({
                voyageNumberList: eventScope.voyageNumbers.join(','),
                imoNumber: selectedImoNumber,
                lastUpdatedUtcEpoch: getOverlappingRecoveryEpoch(eventScope.lastHttpDeltaCursorEpoch),
            }),
            callInBackground: true,
            forceRefreshCache: true,
        })) as TankersEventDataBM | undefined;

        if (!result || !isCurrentRecoveryScope(scope) || selectedVesselRef.current?.imoNumber !== selectedImoNumber) {
            return 'failed';
        }

        const commitRecoveredEventDelta = async (): Promise<void> => {
            if (isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === selectedImoNumber) {
                await applyEventChanges(result, { generation: scope, vesselImoNumber: selectedImoNumber });
            }

            if (!isCurrentRecoveryScope(scope) || selectedVesselRef.current?.imoNumber !== selectedImoNumber) {
                return;
            }

            const currentEventData = eventDataForVoyageListRef.current;
            if (currentEventData?.imoNumber === selectedImoNumber) {
                setEventDataForVoyageList({
                    ...currentEventData,
                    // HTTP response timestamps remain endpoint cursors only. A
                    // SignalR watermark advances exclusively in applyEventChanges
                    // after a validated SignalR message merge succeeds.
                    ...getNextEventRecoveryWatermarks(currentEventData, result.lastUpdatedDateUtc),
                });
            }
        };

        const application = serializeEventApplication({ generation: scope, vesselImoNumber: selectedImoNumber }, commitRecoveredEventDelta);
        if ((result.events?.length ?? 0) > 0) {
            // An Event page might not be mounted on this route. Keep the
            // transaction durable and wait for its real merge acknowledgement
            // in the background, so Stowage and the other recovery adapters
            // are never held behind optional Event UI.
            void application
                .then(() => {
                    if (isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === selectedImoNumber) {
                        setRecoveryDomainState('events', 'current');
                    }
                    // This fires once the transaction is durably acknowledged (committed, or
                    // conclusively determined stale by the tombstone/generation guards) — the real
                    // completion signal, not the earlier synchronous 'pending' return below. A
                    // caller relying on 'pending' alone would treat an in-flight, not-yet-committed
                    // hand-off as success.
                    onEventApplicationSettled?.('committed');
                })
                .catch(() => {
                    if (isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === selectedImoNumber) {
                        setRecoveryDomainState('events', 'failed');
                        setSyncState('failed');
                    }
                    onEventApplicationSettled?.('failed');
                });
            return 'pending';
        }

        try {
            await application;
        } catch {
            onEventApplicationSettled?.('failed');
            return 'failed';
        }
        const recovered = isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === selectedImoNumber;
        onEventApplicationSettled?.(recovered ? 'committed' : 'failed');
        return recovered ? 'recovered' : 'failed';
    };

    const recoverStowage = async (scope: ConnectionGeneration): Promise<RecoveryAdapterResult> => {
        const vessel = selectedVesselRef.current;
        if (!vessel || !isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: vessel.imoNumber, date: new Date() })) {
            return 'not-applicable';
        }

        const result = await stowageMasterPlanVersionRecoveryRef.current.recover(scope, {
            isCurrent: () => isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber,
            getActiveMasterPlanVersion: async () => {
                if (!isCurrentRecoveryScope(scope) || selectedVesselRef.current?.imoNumber !== vessel.imoNumber) {
                    return undefined;
                }
                const masterPlanVersion = await getActiveMasterPlanVersion(vessel.imoNumber);
                return isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber ? masterPlanVersion : undefined;
            },
            reloadMasterGraph: async () => {
                if (!isCurrentRecoveryScope(scope) || selectedVesselRef.current?.imoNumber !== vessel.imoNumber) {
                    return false;
                }
                const loadResult = await getMasterStowagePlanFromApi(
                    vessel,
                    true,
                    () => isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber
                );
                return loadResult.success === true && isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber;
            },
        });
        return result.recovered && isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber ? 'recovered' : 'failed';
    };

    const primeStowageMasterPlanVersion = async (scope: ConnectionGeneration): Promise<void> => {
        const vessel = selectedVesselRef.current;
        if (!vessel || !isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: vessel.imoNumber, date: new Date() })) {
            return;
        }

        const masterPlanVersion = await getActiveMasterPlanVersion(vessel.imoNumber);
        if (masterPlanVersion && isCurrentRecoveryScope(scope) && selectedVesselRef.current?.imoNumber === vessel.imoNumber) {
            stowageMasterPlanVersionRecoveryRef.current.observe(scope, masterPlanVersion);
        }
    };

    const runReconnectRecovery = async (scope: ConnectionGeneration): Promise<void> => {
        const outcome = await reconnectRecoveryCoordinatorRef.current.run(scope, isCurrentRecoveryScope, async () => {
            setSyncState('resyncing');
            setRecoveryDomainState('events', 'pending');
            setRecoveryDomainState('stowage', 'pending');
            setRecoveryDomainState('aolBunker', 'pending');

            const eventsRecovered = await recoverEvents(scope);
            if (!isCurrentRecoveryScope(scope)) {
                return;
            }
            setRecoveryDomainState('events', eventsRecovered === 'recovered' ? 'current' : eventsRecovered === 'failed' ? 'failed' : 'pending');

            const stowageRecovered = await recoverStowage(scope);
            if (!isCurrentRecoveryScope(scope)) {
                return;
            }
            setRecoveryDomainState('stowage', stowageRecovered === 'recovered' ? 'current' : stowageRecovered === 'failed' ? 'failed' : 'pending');

            // Invalidate AOL/Bunker GET caches without automatic refetch so a later
            // user-triggered refresh gets missed changes
            const aolBunkerEndpoints = resolveAolBunkerCacheEndpoints(apiEndpoints);
            await removeRequestsContaining(aolBunkerEndpoints);

            if (!isCurrentRecoveryScope(scope)) {
                return;
            }

            // There is no centralized AOL/Bunker dirty registry. Unknown mounted editable
            // state is intentionally a hard stop: this adapter does not invalidate, fetch,
            // or replace any AOL/Bunker VM, optimistic state, active save, or offline queue.
            const aolBunkerDecision = getEditableRecoveryDecision('unknown');
            setRecoveryDomainState('aolBunker', aolBunkerDecision.canReplaceMountedState ? 'current' : 'blocked-contract');

            if (isAolOrBunkerPath(locationRef.current)) {
                setReconnectNotice(AOL_BUNKER_RECONNECT_NOTICE);
                toastInfo(`${AOL_BUNKER_RECONNECT_NOTICE.title} ${AOL_BUNKER_RECONNECT_NOTICE.message}`, 8000, RECONNECT_NOTICE_TOAST_ID);
            }

            if (eventsRecovered !== 'failed' && stowageRecovered !== 'failed') {
                // The editable AOL/Bunker guard is intentionally unresolved. It is shown as
                // a secondary caveat in the status details, rather than masking healthy
                // API, transport, and vessel-group connectivity.
                setSyncState('blocked-contract');
            } else {
                setSyncState('failed');
            }
        });
        if (outcome === 'failed' && isCurrentRecoveryScope(scope)) {
            setSyncState('failed');
        }
    };

    // A transaction settled without being applied (no Event consumer mounted, or a mounted
    // consumer's backlog overflowed) marks its vessel via markEventReconciliationNeeded rather
    // than discarding the change; this effect re-runs the same authoritative HTTP delta fetch
    // reconnect recovery uses once a consumer is available to converge to current state. The
    // deps are exactly the events that can make reconciliation possible: a consumer becoming
    // available, a fresh miss, or the voyage scope resolving.
    //
    // A failed/rejected fetch retains the flag and does nothing further — no independent
    // retry/backoff loop, since the same deps (a later mount, mark, or reconnect) already
    // re-trigger it. 'pending' means recoverEvents handed the merge to the durable
    // Event-consumer commit path but it hasn't landed yet; the flag can only clear once
    // onEventApplicationSettled reports the real completion, guarded by the generation captured
    // at the start of this attempt so a later mark for the same vessel can't be wiped out by an
    // earlier attempt's stale completion.
    useEffect(() => {
        const imoNumber = selectedVesselRef.current?.imoNumber;
        if (!imoNumber || eventConsumerCount === 0 || !isEventReconciliationNeeded(imoNumber)) {
            return;
        }

        const scope = getCurrentGeneration();
        const attemptGeneration = getEventReconciliationGeneration(imoNumber);

        const onEventApplicationSettled = (outcome: 'committed' | 'failed'): void => {
            if (outcome === 'committed' && isCurrentGeneration(scope) && isEventReconciliationNeeded(imoNumber)) {
                clearEventReconciliationNeeded(imoNumber, attemptGeneration);
            }
        };

        void recoverEvents(scope, onEventApplicationSettled)
            .then((result) => {
                if (result === 'recovered' && isCurrentGeneration(scope) && isEventReconciliationNeeded(imoNumber)) {
                    clearEventReconciliationNeeded(imoNumber, attemptGeneration);
                }
                if (result === 'failed') {
                    setRecoveryDomainState('events', 'failed');
                }
            })
            .catch(() => {
                setRecoveryDomainState('events', 'failed');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventConsumerCount, eventReconciliationVersion, eventDataForVoyageList]);

    const triggerConnectivityRecovery = (): void => {
        const imoNumber = selectedVesselRef.current?.imoNumber;
        if (!imoNumber || !loggedInUserRef.current) {
            return;
        }

        const now = Date.now();
        if (now - lastConnectivityRecoveryAtRef.current < 3000) {
            return;
        }
        lastConnectivityRecoveryAtRef.current = now;

        const connection = hubConnectionRef.current;
        if (connection?.state === HubConnectionState.Connected) {
            recoveryRequiredRef.current = true;
            void joinVesselGroup(connection, imoNumber);
        } else {
            recoveryRequiredRef.current = true;
        }
    };

    // primeStowageMasterPlanVersion is invoked from useSignalRConnection.ts's initial
    // (non-reconnect) join path in joinVesselGroup — establishing a MasterPlanVersion
    // baseline without running full reconnect recovery. See recoverStowage above for the
    // reconnect path.

    useEffect(() => {
        const isDegraded = offlineLevel === OfflineLevelEnum.Offline || offlineLevel === OfflineLevelEnum.BadConnectionDetected || apiUnavailable;

        if (isDegraded) {
            if (hasConnectedOnceRef.current) {
                wasOfflineOrDegradedRef.current = true;
                clearReconnectNotice();
                dismissToast(RECONNECT_NOTICE_TOAST_ID);
            }
            return;
        }

        const isOnline = offlineLevel === OfflineLevelEnum.Online && !apiUnavailable && (typeof navigator === 'undefined' || navigator.onLine !== false);

        if (wasOfflineOrDegradedRef.current && hasConnectedOnceRef.current && isOnline) {
            wasOfflineOrDegradedRef.current = false;
            triggerConnectivityRecovery();
        }
        // Track connectivity transitions (offlineLevel / apiUnavailable) while a vessel session is active.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offlineLevel, apiUnavailable]);

    useEffect(() => {
        const handleOffline = () => {
            if (hasConnectedOnceRef.current) {
                wasOfflineOrDegradedRef.current = true;
                clearReconnectNotice();
                dismissToast(RECONNECT_NOTICE_TOAST_ID);
            }
        };

        const handleOnline = () => {
            const isOnline = offlineLevel === OfflineLevelEnum.Online && !apiUnavailable && (typeof navigator === 'undefined' || navigator.onLine !== false);

            if (wasOfflineOrDegradedRef.current && hasConnectedOnceRef.current && isOnline) {
                wasOfflineOrDegradedRef.current = false;
                triggerConnectivityRecovery();
            }
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
        // Window online/offline events detect immediate browser offline toggles (e.g. DevTools Offline).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offlineLevel, apiUnavailable]);

    return {
        runReconnectRecovery,
        primeStowageMasterPlanVersion,
        applyEventChanges,
    };
};
